// The board's HONEST-STATE notices (brief §5.2): the pre-first-sweep note (the board says when the
// first sweep runs instead of pretending emptiness is health), the sweep-failed attention banner
// (derived from the projection's SweepStamp outcome — the sweep record, G-182), and the over-cap
// banner (what is off the board is NAMED, never dropped silently). Each renders only when its
// projection state holds; machine state rides `data-banner` so tests never assert copy (G-180).

import type { ReactNode } from 'react';
import type { WorkBoardProjection } from '../../projections.ts';

function Notice({
  banner,
  label,
  attention,
  children,
}: {
  readonly banner: string;
  readonly label: string;
  readonly attention: boolean;
  readonly children: ReactNode;
}): React.JSX.Element {
  const tone = attention
    ? 'border-bracken bg-bracken-tint text-text'
    : 'border-border bg-surface-sunken text-text-muted';
  const labelTone = attention ? 'text-bracken' : 'text-text-subtle';
  return (
    <div
      role="status"
      data-banner={banner}
      className={`mb-4 flex items-baseline gap-3 rounded-md border px-4 py-3 text-sm ${tone}`}
    >
      <span className={`ritona-numeric text-xs uppercase tracking-eyebrow ${labelTone}`}>
        {label}
      </span>
      <span>{children}</span>
    </div>
  );
}

export interface BoardNoticesProps {
  readonly board: WorkBoardProjection;
}

/** Zero or more honest notices between the stats and the filters. */
export function BoardNotices({ board }: BoardNoticesProps): React.JSX.Element {
  const sweep = board.lastSweep;
  return (
    <>
      {sweep === null ? (
        <Notice banner="pre-first-sweep" label="First sweep" attention={false}>
          {board.nextDueAt !== null
            ? `Nothing has been swept yet — the first sweep runs ${board.nextDueAt}. Items appear here after it.`
            : 'Nothing has been swept yet — items appear here after the first sweep runs.'}
        </Notice>
      ) : null}
      {sweep !== null && sweep.outcome !== 'completed' ? (
        <Notice banner="sweep-failed" label={`Sweep ${sweep.outcome}`} attention>
          The last sweep ({sweep.at}){' '}
          {sweep.outcome === 'failed' ? 'failed' : 'completed only partially'} — this board may be
          missing fresh evidence until the next run.
        </Notice>
      ) : null}
      {board.overCap > 0 ? (
        <Notice banner="over-cap" label="Over cap" attention>
          <strong className="font-semibold">
            {board.overCap} {board.overCap === 1 ? 'subject' : 'subjects'} beyond the board cap{' '}
            {board.overCap === 1 ? 'isn’t' : 'aren’t'} shown
          </strong>{' '}
          — named here, never dropped silently.
        </Notice>
      ) : null}
    </>
  );
}
